import { useState } from 'react';
import {
  Button,
  Card,
  type CardTone,
  Cluster,
  Columns,
  FoldPanel,
  Input,
  PreviewCard,
  Specimens,
  Stack,
} from '@src/components';

const TONES: { tone: CardTone; label: string }[] = [
  { tone: 'support', label: 'Support' },
  { tone: 'accent', label: 'Accent' },
  { tone: 'info', label: 'Info' },
  { tone: 'success', label: 'Success' },
  { tone: 'warning', label: 'Warning' },
  { tone: 'neutral', label: 'Neutral' },
];

export function CardSection() {
  const [email, setEmail] = useState('');

  return (
    <FoldPanel
      id="cards"
      title="Cards"
      hint="Tinted, not chrome. Type is a darker mix of the same paint as the fill — no black on a pastel. Neutral is the quiet grey one: a hint, not a status."
      example={`<Card tone="info" icon title="Send the link">
  Drop a shared link in the chat.
</Card>

<Card
  tone="info"
  title="Still have questions?"
  action={<Button variant="secondary">FAQ</Button>}
>
  Read the answers on the FAQ page.
</Card>`}
    >
      <Specimens
        label="How it works"
        note="Five tones, one step each. White card, coloured stripe and step disc."
      >
        <Columns columns={2} gap={5}>
          <Card surface="plain" tone="support" step={1} title="Create a game">
            Name it after the group — “Flat 4B” or “Tuesday quiz”.
          </Card>
          <Card surface="plain" tone="accent" step={2} title="Set a budget">
            Agree a sum up front — everyone stays calm, and gifts get easier.
          </Card>
          <Card surface="plain" tone="info" step={3} title="Send the link">
            Drop a shared link in the chat, or invite people one by one.
          </Card>
          <Card surface="plain" tone="success" step={4} title="Run the draw">
            Everyone only sees their giftee. You cannot draw yourself.
          </Card>
          <Card surface="plain" tone="warning" step={5} title="Ask anonymously">
            The built-in chat can check wishes or add intrigue without naming you.
          </Card>
        </Columns>
      </Specimens>

      <Specimens label="Preview card" note="Numbered title and body with seasonal edges.">
        <Columns columns="fill" gap={5}>
          <PreviewCard tone="support" step={1} title="Create a game">
            Name it after the group — “Flat 4B” or “Tuesday quiz”.
          </PreviewCard>
          <PreviewCard tone="accent" step={2} title="Run the draw">
            Invite everyone with a link, then start the draw.
          </PreviewCard>
          <PreviewCard tone="info" step={3} title="Follow the game">
            Watch statuses and help in chat if anyone gets stuck.
          </PreviewCard>
        </Columns>
      </Specimens>

      <Specimens
        label="Decorated form card"
        note="Seasonal edges around a form. slot skips the body type wrap."
      >
        <Stack gap={5} wide>
          <Card surface="plain" edges={{ pieces: 3 }} slot>
            <Stack gap={4} wide>
              <Input
                label="Game name"
                value={email}
                onValueChange={setEmail}
                placeholder="New Year exchange"
              />
              <Cluster justify="between" wide>
                <Button variant="ghost">Back</Button>
                <Button>Continue</Button>
              </Cluster>
            </Stack>
          </Card>
        </Stack>
      </Specimens>

      <Specimens
        label="Plain surface"
        note="Same as the step cards above — border, coloured left stripe, white number on a solid disc."
      >
        <Columns columns="fill" gap={5}>
          <Card surface="plain" tone="support" step={1} title="Create a game">
            Name it after the group — “Flat 4B” or “Tuesday quiz”.
          </Card>
          <Card surface="plain" tone="accent" step={2} title="Run the draw">
            Invite everyone with a link, then start the draw.
          </Card>
          <Card surface="plain" tone="info" step={3} title="Follow the game">
            Watch statuses and help in chat if anyone gets stuck.
          </Card>
        </Columns>
      </Specimens>

      <Specimens
        label="With an action"
        note="Buttons go in the action slot, never in the copy: the slot follows the card's alignment, which is what stops a card reading half-centred. On the right of a wide card, under the copy below 48rem."
      >
        <Stack gap={5} wide>
          <Card
            tone="info"
            title="Still have questions?"
            action={<Button variant="secondary">FAQ</Button>}
          >
            Read the answers on the FAQ page.
          </Card>
          <Card
            tone="info"
            align="center"
            title="Still have questions?"
            action={<Button variant="secondary">FAQ</Button>}
          >
            Read the answers on the FAQ page.
          </Card>
        </Stack>
      </Specimens>

      <Specimens
        label="Tones"
        note="Icon sits on the title row. Body is a step lighter than the title."
      >
        <Columns columns="fill" gap={5}>
          {TONES.map(({ tone, label }) => (
            <Card key={tone} tone={tone} icon title={label}>
              Fill, title and body share one hue.
            </Card>
          ))}
        </Columns>
      </Specimens>

      <Specimens
        label="Body only"
        note="No title — the icon lines up with the first line of the paragraph."
      >
        <Columns columns="fill" gap={5}>
          <Card tone="info" icon>
            The draw is open. Anyone with the link can still join.
          </Card>
          <Card tone="success" icon>
            Everyone has a giftee. You can send the results.
          </Card>
          <Card tone="warning" icon>
            Two people have not opened their card yet.
          </Card>
          <Card tone="neutral" align="center">
            We'll send a one-time link and a six-digit code to sign in. You can change your password
            in profile settings.
          </Card>
        </Columns>
      </Specimens>

      <Specimens
        label="Under a field"
        note="Neutral, no title, centred — the quiet note under an email."
      >
        <Stack gap={4} wide>
          <Input
            label="Your email"
            type="email"
            value={email}
            onValueChange={setEmail}
            placeholder="name@example.com"
          />
          <Card tone="neutral" align="center">
            We'll send a one-time link and a six-digit code to sign in. You can change your password
            in profile settings.
          </Card>
        </Stack>
      </Specimens>
    </FoldPanel>
  );
}
